import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {isAxiosError} from "axios";
import {queryHttp} from "~/query/queryHttp";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import {notificationStore} from "~/stores/init";
import {taxonomyDetailUrl, taxonomyStoreStateUrl} from "~/pages/taxonomy-builder-v2/services/taxonomyBuilderUrls";
import {exportTaxonomyTree} from "~/pages/taxonomy-builder-v2/services/taxonomyTreeEdits";
import {taxonomyHistoryQueryKey} from "~/pages/taxonomy-builder-v2/hooks/useTaxonomyHistory";

export type StoreTaxonomyStateVariables = {
    /** The taxonomy as last loaded: its operation number and next node id are the concurrency check. */
    taxonomy: m_taxonomy.FullSerializer;
    operation: m_taxonomy.Operation;
    state: m_taxonomy.Tree;
    /** Shown once the change is saved. */
    successMessage: string;
};

function errorMessage(error: unknown): string {
    if (isAxiosError(error) && error.response) {
        const data = error.response.data as {message?: string, operation_number?: unknown} | undefined;
        if (data?.message) return `Cannot change the taxonomy. ${data.message}`;
        // Someone else saved first: our operation number is out of date.
        if (data?.operation_number) return 'Cannot change the taxonomy due to a synchronization issue. Please refresh the page.';
    }
    return 'Cannot change the taxonomy. Please try again.';
}

/**
 * `POST taxonomy/{id}/store_state/` with the whole tree, like the current builder does for every edit.
 * The response is the updated taxonomy, so it replaces the cached one straight away.
 */
export function useStoreTaxonomyStateMutation() {
    const queryClient = useQueryClient();

    return useMutation<m_taxonomy.FullSerializer, DefaultError, StoreTaxonomyStateVariables>({
        mutationFn: async ({taxonomy, operation, state}) => {
            const body: m_taxonomy.CreateTaxonomyOperationSerializer = {
                next_node_id: taxonomy.next_node_id + 1,
                operation_number: taxonomy.current_operation_number + 1,
                operation_name: operation.valueOf(),
                state: exportTaxonomyTree(state),
            };
            const {data} = await queryHttp.post<m_taxonomy.FullSerializer>(taxonomyStoreStateUrl(taxonomy.id), body);
            return data;
        },
        onSuccess: (updated, {successMessage}) => {
            queryClient.setQueryData([taxonomyDetailUrl(updated.id)], updated);
            // A new operation drops the redo entries: reload the undo / redo list.
            void queryClient.invalidateQueries({queryKey: taxonomyHistoryQueryKey(updated.id)});
            notificationStore.show(successMessage, {
                severity: 'success',
                id: `taxonomy-store-state-success-${updated.id}`,
                autoHideDuration: 3000,
            });
        },
        onError: (error, {taxonomy}) => {
            notificationStore.show(errorMessage(error), {
                severity: 'error',
                id: `taxonomy-store-state-error-${taxonomy.id}`,
                autoHideDuration: 6000,
            });
        },
    });
}
